import {
  Alert,
  Button,
  Card,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  notification,
} from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { baseUrlDaApiV2 } from "../../utils/useApiV2";
import { describeError, formatDateTime } from "../ai-reports/status";
import ResumoDeContatos, { plural } from "../contatos/ResumoDeContatos";

const { Text, Paragraph } = Typography;
const ROTAS_DO_N8N = [
  {
    metodo: "POST",
    caminho: "/agent/whatsapp/acesso/",
    titulo: "Consulta se o telefone está liberado no canal.",
    corpo: '{"telefone": "..."}',
    respostas: [
      '{"liberado": true, "filial": {"id": 1, "nome": "..."}, "pode_escrever": true}',
      '{"liberado": false, "motivo": "desconhecido | duplicado | fora_da_filial | indisponivel"}',
    ],
  },
  {
    metodo: "POST",
    caminho: "/agent/whatsapp/messages/",
    titulo: "Envia a mensagem do contato ao agente.",
    corpo: '{"telefone": "...", "texto": "...", "id_externo": "..."}',
    respostas: [
      '{"conversation_id": 1, "message_id": 1}',
      '{"ignorado": true}',
    ],
  },
];

const BLOCO = {
  border: "1px solid #e3e8ef",
  borderRadius: 8,
  padding: "12px 14px",
};

const MONO = {
  fontFamily: "SFMono-Regular, Consolas, 'Liberation Mono', monospace",
  fontSize: 13,
};

function CampoDaRota({ rotulo, children }) {
  return (
    <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
      <Text type="secondary" style={{ width: 72, flexShrink: 0, fontSize: 13 }}>
        {rotulo}
      </Text>
      <div style={{ flex: 1, minWidth: 240 }}>{children}</div>
    </div>
  );
}

function RotaDoN8n({ rota }) {
  const url = `${baseUrlDaApiV2()}${rota.caminho}`;
  return (
    <div style={{ ...BLOCO, marginTop: 8 }}>
      <Space size={8} wrap>
        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
          {rota.metodo}
        </Tag>
        <Text copyable={{ text: url }} style={MONO}>
          {url}
        </Text>
      </Space>
      <Text style={{ display: "block", marginTop: 6 }}>{rota.titulo}</Text>
      <CampoDaRota rotulo="Corpo">
        <Text style={MONO}>{rota.corpo}</Text>
      </CampoDaRota>
      <CampoDaRota rotulo="Resposta">
        {rota.respostas.map((resposta) => (
          <Text key={resposta} style={{ ...MONO, display: "block" }}>
            {resposta}
          </Text>
        ))}
      </CampoDaRota>
    </div>
  );
}

export default function WhatsappCard() {
  const {
    getAgentWhatsapp,
    updateAgentWhatsapp,
    generateAgentWhatsappToken,
    getAgentPhones,
  } = useApi();
  const queryClient = useQueryClient();
  const [tokenNovo, setTokenNovo] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [alternando, setAlternando] = useState(false);
  const navigate = useNavigate();

  const canal = useQuery({
    queryKey: ["agent-whatsapp"],
    queryFn: getAgentWhatsapp,
    staleTime: 0,
  });
  const telefones = useQuery({
    queryKey: ["agent-phones", "todos"],
    queryFn: () => getAgentPhones({}),
    staleTime: 0,
  });

  const alternarCanal = async (enabled) => {
    setAlternando(true);
    try {
      const resposta = await updateAgentWhatsapp({ enabled });
      queryClient.setQueryData(["agent-whatsapp"], resposta);
    } catch (err) {
      notification.error({
        message: "Erro ao alterar o canal.",
        description: describeError(err, ""),
      });
    } finally {
      setAlternando(false);
    }
  };

  const gerarToken = async () => {
    setGerando(true);
    try {
      const { token, ...resto } = await generateAgentWhatsappToken();
      queryClient.setQueryData(["agent-whatsapp"], resto);
      setTokenNovo(token);
    } catch (err) {
      notification.error({
        message: "Erro ao gerar o token.",
        description: describeError(err, ""),
      });
    } finally {
      setGerando(false);
    }
  };

  const dados = canal.data;
  const ligado = Boolean(dados?.enabled);
  const conversam = (telefones.data?.telefones || []).filter(
    (t) => t.ativo !== false && t.whatsapp
  );
  const registram = conversam.filter((t) => t.pode_escrever).length;
  const porCliente = [
    ...conversam
      .reduce((mapa, t) => {
        const atual = mapa.get(t.client) || {
          id: t.client,
          nome: t.cliente,
          total: 0,
        };
        mapa.set(t.client, { ...atual, total: atual.total + 1 });
        return mapa;
      }, new Map())
      .values(),
  ].sort((a, b) => a.nome.localeCompare(b.nome));
  const linkDosContatos = (clienteId) =>
    clienteId
      ? `/contatos?cliente=${clienteId}&filtros=agente`
      : "/contatos?filtros=agente";

  return (
    <Card
      title="WhatsApp (n8n)"
      extra={
        <Tooltip
          title={
            dados?.token_prefixo ? "" : "Gere o token do n8n antes de ligar."
          }>
          <Space size={8}>
            <Switch
              aria-label="Canal WhatsApp ligado"
              checked={ligado}
              loading={alternando}
              disabled={!dados?.token_prefixo}
              onChange={alternarCanal}
            />
            <Text>{ligado ? "Canal ligado" : "Canal desligado"}</Text>
          </Space>
        </Tooltip>
      }>
      {ligado && dados && !dados.agente_ligado && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="O agente está desligado. O WhatsApp só responde com o agente e o canal ligados."
        />
      )}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}>
        <div>
          <Text strong style={{ display: "block" }}>
            Token do n8n
          </Text>
          {dados?.token_prefixo ? (
            <Text type="secondary">
              {dados.token_prefixo}… gerado em{" "}
              {formatDateTime(dados.token_gerado_em)}
            </Text>
          ) : (
            <Text type="secondary">Nenhum token gerado.</Text>
          )}
        </div>
        {dados?.token_prefixo ? (
          <Popconfirm
            title="Gerar um novo token?"
            description="O token atual para de funcionar na hora. Atualize o n8n em seguida."
            okText="Gerar"
            cancelText="Cancelar"
            onConfirm={gerarToken}>
            <Button loading={gerando}>Gerar novo token</Button>
          </Popconfirm>
        ) : (
          <Button type="primary" loading={gerando} onClick={gerarToken}>
            Gerar token
          </Button>
        )}
      </div>
      <div style={{ marginTop: 24 }}>
        <Text strong style={{ display: "block" }}>
          Rotas do n8n
        </Text>
        <Text type="secondary">
          Todas pedem o header{" "}
          <Text code>Authorization: Bearer &lt;token&gt;</Text>.
        </Text>
        {ROTAS_DO_N8N.map((rota) => (
          <RotaDoN8n key={rota.caminho} rota={rota} />
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <ResumoDeContatos
          carregando={telefones.isLoading}
          titulo={
            conversam.length
              ? `${plural(conversam.length, "contato conversa", "contatos conversam")} com o agente.`
              : "Nenhum contato conversa com o agente."
          }
          detalhe={
            <>
              {conversam.length
                ? `${plural(registram, "pode", "podem")} registrar. `
                : ""}
              O cadastro e a filial de cada um ficam em Contatos.
              {porCliente.length > 1 && (
                <span style={{ display: "block" }}>
                  {porCliente.map((item, indice) => (
                    <span key={item.id}>
                      {indice > 0 && ", "}
                      <Link to={linkDosContatos(item.id)}>
                        {item.nome} ({item.total})
                      </Link>
                    </span>
                  ))}
                </span>
              )}
            </>
          }
          botao="Ver contatos"
          onAbrir={() =>
            navigate(
              linkDosContatos(
                porCliente.length === 1 ? porCliente[0].id : undefined
              )
            )
          }
        />
      </div>
      <Modal
        open={tokenNovo !== null}
        title="Token do n8n"
        okText="Já copiei"
        cancelButtonProps={{ style: { display: "none" } }}
        closable={false}
        maskClosable={false}
        onOk={() => setTokenNovo(null)}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Copie agora. Este token não aparece de novo."
        />
        <Paragraph copyable code style={{ wordBreak: "break-all" }}>
          {tokenNovo}
        </Paragraph>
      </Modal>
    </Card>
  );
}
